import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Header, Icon, Button, Card, Segment, Label } from 'semantic-ui-react';
import MessageCenter from '../../components/messaging/MessageCenter/MessageCenter';
import { useVoiceLabCompositeData } from '../../hooks/useVoiceLabCompositeData';
import { selectCurrentUser } from '../../slices/userSlice';
import { fetchUserConversations, selectConversations } from '../../slices/messageSlice';
import './MySampleVoices.css';
import { AuthStdFooter } from "../../utils/auth-std-footer";

const MySampleVoices = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useSelector(selectConversations);
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Get demo variations using existing hook with demo filter
  const { variations = [], loading, error } = useVoiceLabCompositeData({
    filters: {
      viewMode: 'DEMO_ONLY',
      usageType: 'PORTFOLIO_DEMO',
      status: 'ACTIVE'
    }
  });

  useEffect(() => {
    if (currentUser?.userId) {
      dispatch(fetchUserConversations(currentUser.userId));
    }
  }, [currentUser?.userId, dispatch]);

  const getTotalUnreadCount = () => {
    return (conversations || []).reduce((total, conv) => total + (conv.unreadCount || 0), 0);
  };

  const getMatchingOpportunities = (variation) => {
    // Mock opportunities for demo - in real app, this would come from opportunity slice
    return [
      {
        id: 'opp_1',
        title: 'Fantasy Audiobook Narrator',
        matchPercentage: 85,
        creatorName: 'Sarah Johnson',
        creatorId: 'user_123'
      },
      {
        id: 'opp_2',
        title: 'Character Voice - Wise Mentor',
        matchPercentage: 92,
        creatorName: 'Mike Chen',
        creatorId: 'user_456'
      }
    ];
  };

  const startOpportunityInquiry = (opportunityId, variationId) => {
    setSelectedOpportunity({ opportunityId, variationId });
    setMessagingOpen(true);
  };

  const hasActiveConversations = (variation) => {
    // Check if this variation has any active conversations
    return (conversations || []).some(conv =>
      conv.threadCount > 0 && conv.unreadCount > 0
    );
  };

  if (loading) {
    return (
      <Segment loading style={{ marginBottom: "70px", minHeight: '400px' }}>
        <p>Loading your sample voices...</p>
      </Segment>
    );
  }

  if (error) {
    return (
      <Segment color="red">
        <Header as="h3">
          <Icon name="exclamation triangle" />
          Error Loading Sample Voices
        </Header>
        <p>{error}</p>
      </Segment>
    );
  }

  return (
    <div className="my-sample-voices">
      <div className="sample-voices-header">
        <div className="header-content">
          <Header as="h2">
            <Icon name="microphone" />
            My Sample Voices
          </Header>
          <p>Showcase your best voice samples to attract opportunities</p>
        </div>

        <div className="header-actions">
          <Button
            icon="comments"
            content={`Messages ${getTotalUnreadCount() > 0 ? `(${getTotalUnreadCount()})` : ''}`}
            color={getTotalUnreadCount() > 0 ? 'blue' : undefined}
            onClick={() => setMessagingOpen(true)}
          />
        </div>
      </div>

      {variations.length === 0 ? (
        <Segment placeholder>
          <Header icon>
            <Icon name="microphone slash" />
            No Demo Samples Yet
          </Header>
          <p>Upload some voice samples to showcase your talent and attract opportunities.</p>
          <Button primary>
            <Icon name="plus" />
            Add Sample Voice
          </Button>
        </Segment>
      ) : (
        <Grid className="demo-portfolio-grid">
          {variations.map(variation => (
            <Grid.Column key={variation.id} width={8} className="demo-variation-column">
              <Card className="demo-variation-card">
                <Card.Content>
                  <Card.Header>
                    {variation.characterName || 'Unnamed Character'}
                    {hasActiveConversations(variation) && (
                      <Label circular color="blue" size="mini" floating>
                        <Icon name="comments" />
                      </Label>
                    )}
                  </Card.Header>

                  <Card.Meta>
                    <span className="variation-type">
                      {variation.variationType || 'General'}
                    </span>
                  </Card.Meta>

                  <Card.Description>
                    <div className="variation-details">
                      <div className="audio-player">
                        {variation.audioUrl ? (
                          <audio controls style={{ width: '100%' }}>
                            <source src={variation.audioUrl} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        ) : (
                          <div className="no-audio">
                            <Icon name="volume off" />
                            No audio sample
                          </div>
                        )}
                      </div>

                      {variation.description && (
                        <p className="variation-description">
                          {variation.description}
                        </p>
                      )}
                    </div>
                  </Card.Description>
                </Card.Content>

                <Card.Content extra>
                  <div className="opportunity-matches">
                    <Header as="h5">
                      <Icon name="target" />
                      Matching Opportunities
                    </Header>

                    {getMatchingOpportunities(variation).map(opportunity => (
                      <div key={opportunity.id} className="opportunity-match">
                        <div className="match-info">
                          <div className="opportunity-title">
                            {opportunity.title}
                          </div>
                          <div className="match-score">
                            {opportunity.matchPercentage}% match
                          </div>
                        </div>

                        <Button
                          size="mini"
                          primary
                          onClick={() => startOpportunityInquiry(opportunity.id, variation.id)}
                        >
                          Inquire
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card.Content>
              </Card>
            </Grid.Column>
          ))}
        </Grid>
      )}

      <MessageCenter
        isOpen={messagingOpen}
        onClose={() => {
          setMessagingOpen(false);
          setSelectedOpportunity(null);
        }}
        initialConversationId={selectedOpportunity?.conversationId}
        initialThreadId={selectedOpportunity?.opportunityId ? `opportunity_${selectedOpportunity.opportunityId}` : null}
      />
      {AuthStdFooter()}
    </div>
  );
};

export default MySampleVoices;
